import { useMemo } from 'react';

import { Combobox, type ComboboxOption } from '../Combobox';

/**
 * EntitySelect — generic searchable picker for "this row references entity X
 * by UUID" fields. Built as a thin layer on top of `<Combobox>`:
 *
 *  - Adds typed `data: T` payload so the consumer's `onChange(value, item)`
 *    receives the original entity (e.g. `(brandId, brand)`).
 *  - Forwards `onSearch` for server-side hooks (debounce stays inside the
 *    consumer hook so we don't fight React Query's cache).
 *  - Supports `disabled` + `disabledReason` (rendered as a helper text) — the
 *    canonical "select a category first to enable subgroups" UX.
 *
 * Wrappers like `VehicleCategorySelect`, `VehicleSubgroupSelect`, etc. wire a
 * specific TanStack Query hook to this component and live in `apps/web` (we
 * keep `@alamo/ui` free of `@alamo/api` so the dependency graph stays acyclic).
 */
export interface EntitySelectOption<T = unknown> {
  value: string;
  label: string;
  hint?: string;
  data?: T;
  disabled?: boolean;
}

export interface EntitySelectProps<T> {
  /** Currently selected UUID, or null when nothing is picked. */
  value: string | null;
  /**
   * Fires with the new UUID and the full entity payload (or null on clear).
   * Consumers integrating with `react-hook-form` should pair this with a
   * `<Controller>` so the form state carries the UUID.
   */
  onChange: (value: string | null, item: T | null) => void;
  /** Pre-mapped options. Wrappers pull from a hook + memoize. */
  options: EntitySelectOption<T>[];
  label?: string;
  placeholder?: string;
  emptyMessage?: string;
  loadingMessage?: string;
  helperText?: string;
  error?: string;
  /** When true, options are still loading from the network. */
  loading?: boolean;
  /** When provided, the consumer is responsible for filtering server-side. */
  onSearch?: (query: string) => void;
  required?: boolean;
  disabled?: boolean;
  /** Text shown as helperText when `disabled` is true (e.g. "Selecione UF antes"). */
  disabledReason?: string;
  clearable?: boolean;
  className?: string;
  name?: string;
  id?: string;
}

export function EntitySelect<T>(props: EntitySelectProps<T>) {
  const {
    value,
    onChange,
    options,
    label,
    placeholder,
    emptyMessage,
    loadingMessage,
    helperText,
    error,
    loading = false,
    onSearch,
    required,
    disabled,
    disabledReason,
    clearable = true,
    className,
    name,
    id,
  } = props;

  // Build the Combobox option list, preserving the data payload for lookup
  // when the consumer's onChange fires.
  const comboboxOptions: ComboboxOption<string>[] = useMemo(
    () =>
      options.map((o) => ({
        value: o.value,
        label: o.label,
        hint: o.hint,
        disabled: o.disabled,
      })),
    [options],
  );

  function handleChange(next: string | null) {
    if (next === null) {
      onChange(null, null);
      return;
    }
    const match = options.find((o) => o.value === next) ?? null;
    onChange(next, (match?.data as T) ?? null);
  }

  const helperTextResolved =
    helperText ?? (disabled && disabledReason ? disabledReason : undefined);

  return (
    <Combobox<string>
      options={comboboxOptions}
      value={value ?? null}
      onChange={handleChange}
      label={label}
      placeholder={placeholder}
      emptyMessage={emptyMessage}
      loadingMessage={loadingMessage}
      helperText={helperTextResolved}
      error={error}
      loading={loading}
      onSearch={onSearch}
      filterByLabel={!onSearch}
      required={required}
      disabled={disabled}
      clearable={clearable}
      className={className}
      name={name}
      id={id}
    />
  );
}
